// libs
import { htmlToText } from 'html-to-text'
import { BorderStyle, ExternalHyperlink, Paragraph, Table, TableCell, TableRow, TextRun, WidthType } from 'docx'
// types
import { ExtendedField } from 'types/risk'
import {
  IField,
  FieldType,
  IFieldGroup,
  IRiskTemplate,
  IFieldValueMapItem,
  IRiskTemplateFormCondition,
  RiskTemplateFormConditionActionType
} from 'types/graphql'
// utils
import { getFieldValue } from 'components/Modals/FieldHistoryModal/components/Table/components/Row/utils'
import { getShouldExecute } from 'hooks/risks/useLogicalConditions/utils'
import { flatten, get, sortBy } from 'lodash'
import { filter, flattenDeep, last, map, pipe, sortBy as sortByFp } from 'lodash/fp'

const borders = {
  top: {
    style: BorderStyle.SINGLE,
    color: '#f2f2f2',
    size: 1
  },
  bottom: {
    style: BorderStyle.SINGLE,
    color: '#f2f2f2',
    size: 1
  },
  left: {
    style: BorderStyle.SINGLE,
    color: '#f2f2f2',
    size: 1
  },
  right: {
    style: BorderStyle.SINGLE,
    color: '#f2f2f2',
    size: 1
  }
}

const margins = {
  top: 100,
  right: 100,
  left: 100,
  bottom: 100,
  marginUnitType: WidthType.DXA
}

const gerPrevRootCol = (field: IField, riskTemplate: IRiskTemplate, tableId: string): Record<string, any> => {
  const fieldLinks = riskTemplate.fieldLinks.filter(({ parentFieldId }) => parentFieldId === tableId)
  const fieldGroups = riskTemplate.fieldGroups.filter(({ parentFieldId }) => parentFieldId === tableId)

  return pipe(
    filter<IFieldGroup>(({ groupedByEntityId }) => groupedByEntityId === field.entityId),
    map<IFieldGroup, string>(({ fieldId }) => fieldId),
    (result) =>
      fieldGroups.filter(
        ({ fieldId, groupedByEntityId }) => result.includes(fieldId) && groupedByEntityId !== field.entityId
      ),
    map<IFieldGroup, any>((fieldGroup) => {
      const fieldLink = fieldLinks.find(
        ({ field: { entityId }, primary }) => primary && entityId === fieldGroup.groupedByEntityId
      )

      return {
        position: get(fieldLink, 'position'),
        id: get(fieldLink, 'field.id')
      }
    }),
    filter(({ position }) => position < field.position),
    sortByFp<any>('position'),
    last
  )(fieldGroups)
}

const generateTableData = ({
  fields,
  values,
  tableId,
  riskTemplate
}: {
  fields: (IField & { fieldLinkId?: string })[]
  values: IFieldValueMapItem[]
  tableId: string
  riskTemplate: IRiskTemplate
}) => {
  const tableData = []

  fields
    .map((item, idx) => ({ ...item, position: idx }))
    .forEach((field) => {
      const fieldValues = values.filter((item) => item.fieldLinkId === field.fieldLinkId)

      if (fieldValues.length > 0) {
        if (!fieldValues[0].parentFieldValueId) {
          // Root field values
          if (fieldValues.length > 1) {
            // @ts-ignore
            fieldValues.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
          }

          fieldValues.forEach((fieldValue, index) => {
            let increment = 0

            const prev = gerPrevRootCol(field, riskTemplate, tableId)

            if (prev) {
              increment = values.filter((item) => item.fieldId === prev.id).length
            }

            const currentIndex = index + increment

            if (!tableData[currentIndex]) {
              tableData[currentIndex] = []
            }
            tableData[currentIndex].push({ ...fieldValue, field })
          })
        } else {
          tableData.forEach((row) => {
            const rowData = []

            row.forEach((rowItem) => {
              if (Array.isArray(rowItem)) {
                rowItem.forEach((item) => {
                  sortBy(
                    fieldValues.filter((fv) => fv.parentFieldValueId === item.fieldValueId),
                    'createdAt'
                  ).forEach((fv) => rowData.push(fv))
                })
              } else {
                sortBy(
                  fieldValues.filter((fv) => fv.parentFieldValueId === rowItem.fieldValueId),
                  'createdAt'
                ).forEach((fv) => rowData.push(fv))
              }
            })

            if (rowData.length) {
              row.push(rowData.map((item) => ({ ...item, field })))
            }
          })
        }
      }
    })

  return tableData
}

const fillArray = (length: number, arrayOfObjects: any) => {
  const result = Array.from({ length }, () => null)

  for (const obj of arrayOfObjects) {
    const position = Array.isArray(obj) ? obj[0].field.position : obj.field.position

    if (position >= length) {
      result[length - 1] = obj
    }

    if (position >= 0 && position < length) {
      result[position] = obj
    }
  }

  return result
}

const getFieldValueNode = (fieldValue: any, field: IField, options?: Record<string, any>) => {
  if (!fieldValue) {
    return new TextRun({
      text: '',
      font: 'Arial',
      ...options
    })
  }

  if (field.fieldType === FieldType.DocumentOrLink) {
    return new ExternalHyperlink({
      children: [
        new TextRun({
          text: fieldValue.title || fieldValue.type.toLowerCase(),
          style: 'Hyperlink',
          font: 'Arial'
        })
      ],
      link: fieldValue.url
    })
  }

  if (field.fieldType === FieldType.RiskMatrix) {
    return new TextRun({
      text: fieldValue?.label || '',
      font: 'Arial',
      color: '#FFFFFF',
      shading: {
        fill: fieldValue?.color
      }
    })
  }

  if (field.fieldType === FieldType.PickList && field.responseTypeId) {
    return new TextRun({
      text: fieldValue?.label || '',
      font: 'Arial',
      color: '#FFFFFF',
      shading: {
        fill: fieldValue?.color
      }
    })
  }

  return new TextRun({
    text: fieldValue || '',
    font: 'Arial',
    ...options
  })
}

const getRowNode = (field: IField, fieldValue: any) => {
  return new TableRow({
    children: [
      new TableCell({
        borders,
        margins,
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: field.hideName ? '' : field.label,
                bold: true,
                font: 'Arial'
              })
            ]
          })
        ]
      }),
      new TableCell({
        borders,
        margins,
        children: [
          new Paragraph({
            children: [getFieldValueNode(fieldValue, field)]
          })
        ],
        ...((field.fieldType === FieldType.RiskMatrix ||
          (field.fieldType === FieldType.PickList && field.responseTypeId)) && {
          shading: {
            fill: fieldValue?.color
          }
        })
      })
    ]
  })
}

const generateSubFields = ({
  fields,
  fieldValues,
  parentFieldValueId
}: {
  fields: IField[]
  fieldValues: IFieldValueMapItem[]
  parentFieldValueId: string
}) => {
  return fields.map((sf) => {
    const fieldValue = fieldValues.find((fv) => fv.parentFieldValueId === parentFieldValueId && fv.fieldId === sf.id)

    if (!fieldValue) {
      return new Paragraph({
        children: [
          new TextRun({
            text: '',
            font: 'Arial'
          })
        ]
      })
    }

    const value = getFieldValue(fieldValue.fieldValue, sf)

    return new Paragraph({
      children: [
        new TextRun({
          text: `${fieldValue.fieldLabel}: `,
          bold: true,
          break: 1,
          font: 'Arial'
        }),
        getFieldValueNode(value, sf)
      ]
    })
  })
}

const generateTable = ({
  fields,
  tableId,
  fieldValues,
  riskTemplate
}: {
  fields: ExtendedField[]
  tableId: string
  fieldValues: IFieldValueMapItem[]
  riskTemplate: IRiskTemplate
}) => {
  const tableFields =
    sortBy(
      fields.filter((f) => f.parentFieldId === tableId && f.primary),
      'position'
    ) || []

  const tableData = generateTableData({ fields: tableFields, values: fieldValues, riskTemplate, tableId }).map((row) =>
    fillArray(tableFields.length, row)
  )

  const tableHeader = new TableRow({
    children: tableFields.map(
      (field) =>
        new TableCell({
          borders,
          margins,
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: field.label,
                  bold: true,
                  font: 'Arial'
                })
              ]
            })
          ],
          shading: {
            fill: '#bfbfbf'
          }
        })
    )
  })

  if (!tableData.length) {
    return new Paragraph({
      children: [new TextRun({ text: '' })]
    })
  }

  return new Table({
    columnWidths: tableFields.map((_i) => 14000 / tableFields.length),
    rows: [
      tableHeader,
      ...tableData.map(
        (row) =>
          new TableRow({
            children: row.map((fieldValue) => {
              if (!fieldValue) {
                return new TableCell({
                  borders,
                  margins,
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: '',
                          font: 'Arial'
                        })
                      ]
                    })
                  ]
                })
              }

              if (Array.isArray(fieldValue)) {
                return new TableCell({
                  borders,
                  margins,
                  children: flatten(
                    fieldValue.map((item, idx) => {
                      const field = fields.find(({ id }) => id === item.fieldId)

                      const value = item.fieldValue ? getFieldValue(item.fieldValue, field) : ''

                      const secondaryFields = sortBy(
                        fields.filter(
                          (f) => f.parentFieldId === tableId && !f.primary && f.entityId === field.entityId
                        ),
                        'position'
                      )

                      if (secondaryFields.length) {
                        return [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: `${item.fieldLabel}: `,
                                bold: true,
                                break: idx > 0 ? 1 : 0,
                                font: 'Arial'
                              }),
                              getFieldValueNode(value, field)
                            ]
                          }),
                          ...generateSubFields({
                            fieldValues,
                            fields: secondaryFields,
                            parentFieldValueId: item.fieldValueId
                          })
                        ]
                      }

                      return new Paragraph({
                        children: [getFieldValueNode(value, field, { break: idx > 0 ? 2 : 0 })]
                      })
                    })
                  )
                })
              }

              const field = fieldValue.field

              const value = getFieldValue(fieldValue.fieldValue, field)

              const secondaryFields = fields.filter(
                (f) => f.parentFieldId === tableId && !f.primary && f.entityId === field.entityId
              )

              if (secondaryFields.length) {
                return new TableCell({
                  borders,
                  margins,
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `${fieldValue.fieldLabel}: `,
                          bold: true,
                          font: 'Arial'
                        }),
                        getFieldValueNode(value, field)
                      ]
                    }),
                    ...generateSubFields({
                      fields: secondaryFields,
                      fieldValues,
                      parentFieldValueId: fieldValue.fieldValueId
                    })
                  ]
                })
              }

              return new TableCell({
                borders,
                ...(field.fieldType === FieldType.RiskMatrix && {
                  shading: {
                    fill: fieldValue?.color
                  }
                }),
                children: [
                  new Paragraph({
                    children: [getFieldValueNode(value, field)]
                  })
                ]
              })
            })
          })
      )
    ]
  })
}

const generateLabel = (field: IField) => {
  return new TableRow({
    children: [
      new TableCell({
        margins,
        columnSpan: 2,
        borders: {
          ...borders,
          left: {
            style: BorderStyle.NONE
          },
          right: {
            style: BorderStyle.NONE
          }
        },
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: field.wysiwyg
                  ? htmlToText(field.label, {
                      wordwrap: false
                    })
                  : field.label,
                bold: true,
                size: 26,
                font: 'Arial'
              })
            ]
          })
        ]
      })
    ]
  })
}

const generateSection = (
  sectionFields: IField[],
  fields: ExtendedField[],
  fieldValues: IFieldValueMapItem[],
  riskTemplate: IRiskTemplate
) => {
  return sectionFields.map((field) => generateField({ field, fields, fieldValues, riskTemplate }))
}

const generateField = ({
  field,
  fields,
  riskTemplate,
  fieldValues
}: {
  field: IField
  fields: ExtendedField[]
  riskTemplate: IRiskTemplate
  fieldValues: IFieldValueMapItem[]
}) => {
  const fieldValue = fieldValues.find(({ fieldId }) => fieldId === field.id)

  const value = fieldValue ? getFieldValue(fieldValue.fieldValue, field) : ''

  if (field.fieldType === FieldType.Label) {
    return generateLabel(field)
  }

  if (field.fieldType === FieldType.InjuryMasterLinks || field.fieldType === FieldType.InjuryMasterParentLinks) {
    return []
  }

  if (field.fieldType === FieldType.Section) {
    const sectionFields = fields.filter((f) => f.parentFieldId === field.id && !f.fieldLinkId)

    return [
      new TableRow({
        children: [
          new TableCell({
            margins,
            columnSpan: 2,
            borders: {
              ...borders,
              left: {
                style: BorderStyle.NONE
              },
              right: {
                style: BorderStyle.NONE
              }
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: field.label,
                    bold: true,
                    ...(!field.parentFieldId && { size: 26 }),
                    font: 'Arial'
                  })
                ]
              })
            ]
          })
        ]
      }),
      ...generateSection(sectionFields, fields, fieldValues, riskTemplate)
    ]
  }

  if (field.fieldType === FieldType.Table) {
    const tableFields = fields.filter((f) => f.parentFieldId === field.id && !f.fieldLinkId)

    return [
      ...tableFields.map((tf) => {
        const fieldValue = fieldValues.find(({ fieldId }) => fieldId === tf.id)

        const value = fieldValue ? getFieldValue(fieldValue.fieldValue, tf) : ''

        return getRowNode(tf, value)
      }),
      new TableRow({
        children: [
          new TableCell({
            margins,
            columnSpan: 2,
            borders: {
              ...borders,
              left: {
                style: BorderStyle.NONE
              },
              right: {
                style: BorderStyle.NONE
              }
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: field.label,
                    bold: true,
                    ...(!field.parentFieldId && { size: 26 }),
                    font: 'Arial'
                  })
                ]
              }),
              generateTable({ tableId: field.id, fields, fieldValues, riskTemplate })
            ]
          })
        ]
      })
    ]
  }

  return getRowNode(field, value)
}

export const generateFields = ({
  fields,
  fieldValues,
  riskTemplate
}: {
  fields: ExtendedField[]
  fieldValues: IFieldValueMapItem[]
  riskTemplate: IRiskTemplate
}) => {
  return new Table({
    rows: flattenDeep(
      fields
        .filter((item) => !item.fieldLinkId && !item.parentFieldId)
        .map((field) =>
          generateField({
            field,
            fields,
            fieldValues,
            riskTemplate
          })
        )
    ),
    width: {
      size: 100,
      type: WidthType.PERCENTAGE
    },
    margins: {
      top: 0,
      left: 0,
      marginUnitType: WidthType.DXA
    },
    borders,
    columnWidths: [4000, 10000]
  })
}

const applyConditions = (field: IField, fieldValues: Record<string, any>, conditions: IRiskTemplateFormCondition[]) => {
  const formattedField = { ...field }

  if (conditions.every((condition) => condition.actions.every((action) => action.fieldId !== field.id))) {
    return formattedField
  }

  conditions?.forEach((condition) => {
    const fieldValue = fieldValues.find(({ fieldId }) => fieldId === condition.fieldId)?.fieldValue

    if (condition.actions.every((action) => action.fieldId !== field.id)) {
      return
    }

    const shouldExecute = getShouldExecute(condition, fieldValue)

    if (shouldExecute) {
      condition.actions.forEach((action) => {
        if (action.fieldId === field.id) {
          if (action.type === RiskTemplateFormConditionActionType.Visibility) {
            formattedField.hidden = action.value
          }
        }
      })
    }
  })

  return formattedField
}

export const applyLogicalConditions = (
  fields: IField[],
  fieldValues: Record<string, any>,
  formConditions: IRiskTemplateFormCondition[]
) => {
  let preparedFields = fields.map((field) => applyConditions(field, fieldValues, formConditions))
  let curFormValues = fieldValues
  let prevFormValues

  do {
    prevFormValues = curFormValues

    const visibleFields = preparedFields.filter((item) => {
      const isVisible = !item.hidden
      const isParentVisible = item.parentFieldId
        ? preparedFields.find((f) => f.id === item.parentFieldId)?.hidden === false
        : true

      return isVisible && isParentVisible
    })

    curFormValues = curFormValues.filter(({ fieldId }) => visibleFields.some((f) => f.id === fieldId))

    preparedFields = fields.map((field) => {
      return applyConditions(field, curFormValues, formConditions)
    })
  } while (JSON.stringify(curFormValues) !== JSON.stringify(prevFormValues))

  return preparedFields.filter((field) => !field.hidden)
}
